import { useEffect, useState } from 'react';
import { useSubscription } from '@/contexts/SubscriptionContext';
import { Button } from '@/components/ui/button';
import { Crown, Clock, Info } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';

export const TrialBanner = () => {
  const { isTrialing, isFreeExploring, subscribed, subscriptionEnd, freeAccessEndsAt } = useSubscription();
  const navigate = useNavigate();
  const [daysLeft, setDaysLeft] = useState<number | null>(null);
  const [isFreePeriod, setIsFreePeriod] = useState(false);

  useEffect(() => {
    if (isFreeExploring && freeAccessEndsAt) {
      const diff = new Date(freeAccessEndsAt).getTime() - Date.now();
      setDaysLeft(Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24))));
      setIsFreePeriod(true);
      return;
    }
    if (isTrialing && subscriptionEnd) {
      const diff = new Date(subscriptionEnd).getTime() - Date.now();
      setDaysLeft(Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24))));
      setIsFreePeriod(false);
    }
  }, [isTrialing, isFreeExploring, subscriptionEnd, freeAccessEndsAt]);

  if (subscribed || (!isTrialing && !isFreeExploring) || daysLeft === null) {
    return null;
  }

  const isUrgent = daysLeft <= 2;
  const totalDays = isFreePeriod ? 5 : 14;
  const progress = Math.min(100, ((totalDays - daysLeft) / totalDays) * 100);

  const headline = isFreePeriod
    ? daysLeft === 0
      ? 'Your free access ends today'
      : `${daysLeft} ${daysLeft === 1 ? 'day' : 'days'} left of free access`
    : daysLeft === 0
      ? 'Your trial ends today'
      : `${daysLeft} ${daysLeft === 1 ? 'day' : 'days'} left in your free trial`;

  const subline = isFreePeriod
    ? 'Then start a 14-day free trial — no charge until day 15.'
    : 'Subscribe now to keep uninterrupted access after your trial.';

  return (
    <div
      className={`mb-6 rounded-xl border p-4 sm:p-5 ${
        isUrgent
          ? 'border-destructive/40 bg-destructive/5'
          : 'border-primary/30 bg-primary/5'
      }`}
    >
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-start gap-3 flex-1">
          <div
            className={`flex flex-col items-center justify-center rounded-lg px-3 py-2 flex-shrink-0 ${
              isUrgent ? 'bg-destructive/10' : 'bg-primary/10'
            }`}
          >
            <span
              className={`text-2xl font-bold leading-none ${
                isUrgent ? 'text-destructive' : 'text-primary'
              }`}
            >
              {daysLeft}
            </span>
            <span className="text-[10px] uppercase tracking-wide text-muted-foreground mt-0.5">
              {daysLeft === 1 ? 'day' : 'days'}
            </span>
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              {isUrgent ? (
                <Clock className="h-4 w-4 text-destructive flex-shrink-0" />
              ) : (
                <Crown className="h-4 w-4 text-primary flex-shrink-0" />
              )}
              <p className="font-serif text-base font-semibold text-foreground">
                {headline}
              </p>
              <Popover>
                <PopoverTrigger asChild>
                  <button
                    className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground underline underline-offset-2"
                    aria-label="What happens if I don't upgrade?"
                  >
                    <Info className="h-3 w-3" />
                    What happens next?
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-80 text-sm space-y-2" align="start">
                  <p className="font-semibold text-foreground">If you don't upgrade:</p>
                  <ul className="space-y-1.5 text-muted-foreground list-disc pl-4">
                    {isFreePeriod ? (
                      <>
                        <li>After day 5, access to premium features pauses.</li>
                        <li>Your data (tracker logs, notes, symptoms) stays saved.</li>
                        <li>Start the 14-day free trial anytime — cancel before day 14 and pay nothing.</li>
                      </>
                    ) : (
                      <>
                        <li>Without a subscription, premium features pause after day 14.</li>
                        <li>Your saved data, logs, and history remain intact.</li>
                        <li>You can subscribe later to restore full access at any time.</li>
                        <li>Cancel before day 14 to avoid any charge.</li>
                      </>
                    )}
                  </ul>
                </PopoverContent>
              </Popover>
            </div>
            <p className="text-sm text-muted-foreground mt-1">{subline}</p>
            <div className="mt-3 h-1.5 w-full rounded-full bg-muted overflow-hidden">
              <div
                className={`h-full transition-all ${
                  isUrgent ? 'bg-destructive' : 'bg-primary'
                }`}
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        </div>
        <Button
          onClick={() => navigate('/pricing')}
          size="sm"
          className="bg-gradient-to-r from-primary to-primary/80 flex-shrink-0 w-full sm:w-auto"
        >
          {isFreePeriod ? 'Start Free Trial' : 'Subscribe Now'}
        </Button>
      </div>
    </div>
  );
};
